import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import "../../assets/css/terms.css";

const sections = [
  {
    id: "acceptance",
    num: "01",
    icon: "bi-file-earmark-check",
    heading: "Acceptance of Terms",
    content: (
      <>
        <p>
          By accessing or using Zoomey — including creating an account, signing in, or
          interacting with the AI assistant in any way — you confirm that you have read,
          understood, and agree to be bound by these Terms and Conditions and our Privacy Policy.
        </p>
        <div className="tc-callout tc-callout--info">
          <div className="tc-callout-icon"><i className="bi bi-info-circle-fill"></i></div>
          <div>
            <strong>Important:</strong> If you do not agree with any part of these Terms,
            you must immediately discontinue use of the Zoomey platform and services.
          </div>
        </div>
        <p>
          These Terms constitute a legally binding agreement between you and <strong>Playces.in</strong>,
          the entity operating the Zoomey platform.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    num: "02",
    icon: "bi-shield-check",
    heading: "Acceptable Use",
    content: (
      <>
        <p>
          You may use Zoomey only for lawful, personal or professional purposes consistent
          with these Terms. The following activities are strictly prohibited:
        </p>
        <div className="tc-rule-list">
          {[
            { icon: "bi-person-x", text: "Using the service to harm, harass, stalk, deceive, or defraud other individuals." },
            { icon: "bi-lock-fill", text: "Attempting to gain unauthorized access to any part of the system, other user accounts, or backend infrastructure." },
            { icon: "bi-robot", text: "Using automated tools, bots, crawlers, or scripts to scrape content, overload servers, or abuse the service." },
            { icon: "bi-key-fill", text: "Sharing, selling, renting, or transferring your account credentials to any third party." },
            { icon: "bi-exclamation-octagon", text: "Generating, distributing, or storing illegal, harmful, hateful, or sexually offensive content." },
            { icon: "bi-bug", text: "Introducing malware, viruses, or any code designed to damage or disrupt the service." },
          ].map((rule, i) => (
            <div key={i} className="tc-rule-item">
              <div className="tc-rule-icon"><i className={`bi ${rule.icon}`}></i></div>
              <span>{rule.text}</span>
            </div>
          ))}
        </div>
        <p>
          Violation of these rules may result in immediate account suspension or permanent
          termination without prior notice.
        </p>
      </>
    ),
  },
  {
    id: "account",
    num: "03",
    icon: "bi-person-badge",
    heading: "Account Responsibility",
    content: (
      <>
        <p>
          You are solely and entirely responsible for maintaining the confidentiality of
          your login credentials and for all activities, actions, and transactions that
          occur under your account.
        </p>
        <div className="tc-two-col">
          <div className="tc-info-card">
            <div className="tc-info-card-icon"><i className="bi bi-shield-lock"></i></div>
            <div className="tc-info-card-title">Keep credentials secure</div>
            <div className="tc-info-card-text">Never share your password. Use a strong, unique password for your Zoomey account.</div>
          </div>
          <div className="tc-info-card">
            <div className="tc-info-card-icon"><i className="bi bi-bell"></i></div>
            <div className="tc-info-card-title">Report unauthorized access</div>
            <div className="tc-info-card-text">Contact <a href="mailto:support@playces.in">support@playces.in</a> immediately if you suspect unauthorized access.</div>
          </div>
        </div>
        <p>
          We reserve the right to suspend or permanently terminate accounts that we
          reasonably believe have violated these Terms or pose a risk to other users
          or the platform.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    num: "04",
    icon: "bi-c-circle",
    heading: "Intellectual Property",
    content: (
      <>
        <p>
          All content, features, and functionality made available through Zoomey —
          including but not limited to software, code, interface design, text, graphics,
          icons, logos, product names, and the overall look and feel — are the exclusive
          property of <strong>Playces.in</strong> and are protected under applicable
          copyright, trademark, patent, and other intellectual property laws.
        </p>
        <div className="tc-callout tc-callout--warning">
          <div className="tc-callout-icon"><i className="bi bi-exclamation-triangle-fill"></i></div>
          <div>
            You may <strong>not</strong> copy, reproduce, modify, distribute, reverse-engineer,
            or create derivative works from any part of the Zoomey platform without prior
            explicit written permission from Playces.in.
          </div>
        </div>
        <p>
          Any feedback, suggestions, or ideas you provide regarding Zoomey may be used
          by us freely without any obligation to compensate you.
        </p>
      </>
    ),
  },
  {
    id: "ai-disclaimer",
    num: "05",
    icon: "bi-cpu",
    heading: "AI Content Disclaimer",
    content: (
      <>
        <p>
          Zoomey is powered by artificial intelligence and large language models to
          generate responses, suggestions, and content. You acknowledge and agree to
          the following:
        </p>
        <div className="tc-rule-list">
          {[
            { icon: "bi-patch-question", text: "AI-generated responses may contain inaccuracies, outdated information, or factual errors." },
            { icon: "bi-heart-pulse", text: "Zoomey's output must NOT be used as a substitute for professional medical, legal, financial, or safety-critical advice." },
            { icon: "bi-person-check", text: "You are solely responsible for verifying any AI-generated content before acting on it." },
            { icon: "bi-arrow-repeat", text: "AI model behavior may change over time as we improve the underlying technology." },
          ].map((rule, i) => (
            <div key={i} className="tc-rule-item">
              <div className="tc-rule-icon"><i className={`bi ${rule.icon}`}></i></div>
              <span>{rule.text}</span>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: "liability",
    num: "06",
    icon: "bi-balance-scale",
    heading: "Limitation of Liability",
    content: (
      <>
        <p>
          Zoomey is provided on an <strong>"as-is"</strong> and <strong>"as-available"</strong> basis.
          Playces.in makes no warranties, express or implied, regarding the accuracy,
          reliability, completeness, or fitness of the service for any particular purpose.
        </p>
        <div className="tc-callout tc-callout--warning">
          <div className="tc-callout-icon"><i className="bi bi-shield-exclamation"></i></div>
          <div>
            To the maximum extent permitted by applicable law, <strong>Playces.in shall not be
            liable</strong> for any indirect, incidental, special, consequential, exemplary, or
            punitive damages — including but not limited to loss of data, revenue, profits,
            or business opportunities — arising from your use of or inability to use Zoomey.
          </div>
        </div>
        <p>
          Our total aggregate liability to you for any claim arising out of these Terms
          shall not exceed the amount paid by you (if any) to access Zoomey in the
          twelve (12) months preceding the claim.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    num: "07",
    icon: "bi-slash-circle",
    heading: "Termination",
    content: (
      <>
        <p>
          Either party may terminate this agreement at any time. You may delete your
          account through your profile settings. We may suspend or terminate your access
          if we determine, in our sole discretion, that you have violated these Terms.
        </p>
        <p>
          Upon termination, your right to use the service immediately ceases. Provisions
          of these Terms that by their nature should survive termination will continue
          to apply, including intellectual property rights and limitation of liability clauses.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    num: "08",
    icon: "bi-arrow-repeat",
    heading: "Changes to Terms",
    content: (
      <>
        <p>
          We reserve the right to modify, update, or replace these Terms and Conditions
          at any time at our sole discretion. We will provide notice of material changes
          by updating the effective date shown at the top of this page.
        </p>
        <p>
          Your continued access to or use of Zoomey after revised Terms are posted
          constitutes your acceptance of those changes. If you disagree with the
          modified Terms, you must stop using the service.
        </p>
        <div className="tc-callout tc-callout--info">
          <div className="tc-callout-icon"><i className="bi bi-bell-fill"></i></div>
          <div>
            We recommend bookmarking this page and reviewing it periodically to stay
            informed of any updates.
          </div>
        </div>
      </>
    ),
  },
];

const TermsAndConditions = () => {
  const [activeId, setActiveId] = useState(sections[0].id);
  const scrollRef = useRef(null);
  const isDarkMode = Cookies.get("isDarkMode") === "true";

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { root: container, rootMargin: "-10% 0px -40% 0px", threshold: 0 }
    );
    sections.forEach(({ id }) => {
      const el = container.querySelector(`#${id}`);
      if (el) observer.observe(el);
    });

    // Activate last section when scrolled to bottom
    const lastId = sections[sections.length - 1].id;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 40) {
        setActiveId(lastId);
      }
    };
    container.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      container.removeEventListener("scroll", onScroll);
    };
  }, []);

  const scrollTo = (id) => {
    const container = scrollRef.current;
    const el = container?.querySelector(`#${id}`);
    if (!el || !container) return;
    const top = el.offsetTop - 24;
    container.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div className="tc-page">

      {/* ── Top Navigation ── */}
      <nav className="tc-nav">
        <div className="tc-nav-inner">
          <Link to="/login" className="zoomey-logo-wrapper">
            <div className={`zoomey-logo-mark ${isDarkMode ? "dark" : "light"}`}>
              <div className="logo-glow"></div>
              <div className="logo-inner">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 7H22L8 21H22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="z-path" />
                  <circle cx="22" cy="7" r="2" fill="currentColor" className="z-dot" />
                </svg>
              </div>
            </div>
            <div className="zoomey-brand-text">
              <span className={`brand-name ${isDarkMode ? "dark" : "light"}`}>ZOOMEY</span>
            </div>
          </Link>
          <div className="tc-nav-actions">
            <Link to="/pricing" className="tc-nav-link">Pricing</Link>
            <Link to="/faq" className="tc-nav-link">FAQ</Link>
            <Link to="/privacy-policy" className="tc-nav-link">Privacy Policy</Link>
          </div>
        </div>
      </nav>

      {/* ── Main Layout: fixed sidebar + scrollable right ── */}
      <div className="tc-layout">

        {/* LEFT — Fixed Sidebar */}
        <aside className="tc-sidebar">
          <p className="tc-sidebar-label">Table of Contents</p>
          <nav className="tc-sidebar-nav">
            {sections.map(({ id, num, heading }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`tc-toc-item ${activeId === id ? "active" : ""}`}
              >
                <span className="tc-toc-num">{num}</span>
                <span className="tc-toc-text">{heading}</span>
                {activeId === id && <span className="tc-toc-dot"></span>}
              </button>
            ))}
          </nav>

          <div className="tc-sidebar-contact">
            <p className="tc-sidebar-contact-label">Legal questions?</p>
            <a href="mailto:legal@playces.in" className="tc-sidebar-contact-link">
              <i className="bi bi-envelope"></i>
              legal@playces.in
            </a>
          </div>
        </aside>

        {/* RIGHT — Scrollable area */}
        <div className="tc-right-scroll" ref={scrollRef}>

          {/* Hero Banner */}
          <header className="tc-hero">
            <div className="tc-hero-bg-grid"></div>
            <div className="tc-hero-inner">
              <div className="tc-hero-badge">
                <i className="bi bi-file-earmark-text"></i>
                Legal Document
              </div>
              <h1 className="tc-hero-title">Terms &amp; Conditions</h1>
              <p className="tc-hero-sub">
                These terms govern your use of the Zoomey AI personal assistant platform
                operated by Playces.in. Please read carefully.
              </p>
              <div className="tc-hero-chips">
                <div className="tc-chip">
                  <i className="bi bi-calendar-check"></i>
                  Effective: April 6, 2026
                </div>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="tc-content">
            <div className="tc-intro-box">
              <div className="tc-intro-icon"><i className="bi bi-info-circle"></i></div>
              <div>
                By accessing or using Zoomey, you agree to be bound by these Terms and
                Conditions. These terms apply alongside our{" "}
                <Link to="/privacy-policy">Privacy Policy</Link>. Questions?{" "}
                <a href="mailto:legal@playces.in">legal@playces.in</a>
              </div>
            </div>

            {sections.map(({ id, num, icon, heading, content }) => (
              <section key={id} id={id} className="tc-section">
                <div className="tc-section-header">
                  <div className="tc-section-num">{num}</div>
                  <div className="tc-section-title-wrap">
                    <div className="tc-section-icon-box">
                      <i className={`bi ${icon}`}></i>
                    </div>
                    <h2 className="tc-section-title">{heading}</h2>
                  </div>
                </div>
                <div className="tc-section-body">{content}</div>
              </section>
            ))}

            <div className="tc-last-updated">
              <i className="bi bi-patch-check"></i>
              <span>Last reviewed on <strong>April 6, 2026</strong>. Previous versions available upon request.</span>
            </div>
          </main>

          {/* Footer inside scroll area */}
          <footer className="tc-footer">
            <div className="tc-footer-inner">
              <div className="tc-footer-left">
                <span className="tc-footer-brand">ZOOMEY</span>
                <span>© 2026 Playces.in. All rights reserved.</span>
              </div>
              <div className="tc-footer-links">
                <Link to="/pricing">Pricing</Link>
                <Link to="/faq">FAQ</Link>
                <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
                <Link to="/privacy-policy">Privacy Policy</Link>
                <a href="mailto:legal@playces.in">Contact</a>
                <Link to="/login">Sign In</Link>
              </div>
            </div>
          </footer>

        </div>{/* end .tc-right-scroll */}
      </div>{/* end .tc-layout */}

    </div>
  );
};

export default TermsAndConditions;
